import { errorMessage } from '../i18n/errorMessages';
import type { MessageDescriptor } from '@lingui/core';
import { msg } from '@lingui/core/macro';
import { Trans, Plural, useLingui } from '@lingui/react/macro';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { EllipsisOutlined, IdcardOutlined, MessageOutlined, PlusOutlined } from '@ant-design/icons';
import { Alert, Button, Modal, Popover } from 'antd';
import { useServerStore, useAgentStore, useAuthStore } from '../stores';
import { addGuildMember, fetchAgents, type Agent } from '../lib/api';
import InviteLinksModal from './InviteLinksModal';
import { UserBadge } from './UserBadge';
import { DISABLED_LABEL } from '../lib/agent-state';
import ServerMemberRemovalControl from './ServerMemberRemovalControl';
import ProjectMemberRoleControl from './ProjectMemberRoleControl';
import MemberProjectProfilePopover from './MemberProjectProfilePopover';
import surface from './ProjectSurface.module.css';
import styles from './ProjectMembersPage.module.css';
import ProjectPageHeader from './ProjectPageHeader';
import { useProjectAuthority } from '../hooks/useProjectAuthority';
import type { GuildMember } from '../lib/api';

interface Props {
  selectedMemberId: string | null;
  onSelectMember: (id: string) => void;
}

const membershipLabel = (member: GuildMember, ownerId: string | undefined): MessageDescriptor => {
  if (member.role === 'agent') return msg`Agent`;
  if (member.user_id === ownerId) return msg`Owner`;
  if (member.membership_role === 'admin') return msg`Admin`;
  return msg`Member`;
};

interface AddOwnedAgentDialogProps {
  guildId: string;
  currentUserId: string;
  memberIds: Set<string>;
  onClose: () => void;
}

function AddOwnedAgentDialog({
  guildId,
  currentUserId,
  memberIds,
  onClose,
}: AddOwnedAgentDialogProps) {
  const { t, i18n } = useLingui();
  const loadMembers = useServerStore((s) => s.loadMembers);
  const [initialMemberIds] = useState(() => new Set(memberIds));
  const [agents, setAgents] = useState<Agent[]>([]);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<MessageDescriptor | null>(null);
  const [submitError, setSubmitError] = useState<MessageDescriptor | null>(null);
  const [refreshError, setRefreshError] = useState<MessageDescriptor | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const generation = useRef(0);
  const submissionInFlight = useRef(false);

  const loadCandidates = useCallback(async () => {
    const request = ++generation.current;
    setLoading(true);
    setLoadError(null);
    setSubmitError(null);
    setRefreshError(null);
    setSelectedId(null);
    setAgents([]);
    try {
      const freshAgents = await fetchAgents();
      if (request !== generation.current) return;
      setAgents(
        freshAgents.filter(
          (agent) =>
            agent.owner_id === currentUserId && !agent.disabled && !initialMemberIds.has(agent.id),
        ),
      );
    } catch (error) {
      if (request !== generation.current) return;
      setLoadError(errorMessage(error, msg`Could not load your agents.`));
    } finally {
      if (request === generation.current) setLoading(false);
    }
  }, [currentUserId, initialMemberIds]);

  useEffect(() => {
    void loadCandidates();
    return () => {
      generation.current++;
    };
  }, [loadCandidates]);

  const refreshRoster = async (request = ++generation.current) => {
    setRefreshError(null);
    try {
      const result = await loadMembers(guildId);
      if (request !== generation.current) return;
      if (result.status === 'applied') {
        onClose();
        return;
      }
      setRefreshError(
        msg`The Project member list refresh was superseded. Retry refresh to confirm the updated roster.`,
      );
    } catch (error) {
      if (request === generation.current)
        setRefreshError(
          errorMessage(
            error,
            msg`The agent was added, but the Project member list could not be refreshed.`,
          ),
        );
    }
  };

  const handleCancel = () => {
    if (submitting || submissionInFlight.current) return;
    onClose();
  };

  const submit = async () => {
    // React state updates are asynchronous, so use a ref to reject two clicks
    // that occur before the button has re-rendered as disabled.
    if (!selectedId || submitting || submissionInFlight.current || refreshError) return;
    submissionInFlight.current = true;
    const request = ++generation.current;
    setSubmitting(true);
    setSubmitError(null);
    try {
      await addGuildMember(guildId, selectedId);
      if (request !== generation.current) return;
      await refreshRoster(request);
    } catch (error) {
      if (request === generation.current)
        setSubmitError(errorMessage(error, msg`Could not add the agent to this Project.`));
    } finally {
      submissionInFlight.current = false;
      if (request === generation.current) setSubmitting(false);
    }
  };

  return (
    <Modal
      open
      title={t`Add Agent`}
      onCancel={handleCancel}
      closable={!submitting}
      keyboard={!submitting}
      mask={{ closable: !submitting }}
      footer={null}
      destroyOnHidden
    >
      <p id="add-owned-agent-description">
        <Trans>Choose one of your active Agents to add to this Project.</Trans>
      </p>
      {loading && (
        <p role="status">
          <Trans>Loading your Agents…</Trans>
        </p>
      )}
      {loadError && (
        <Alert
          type="error"
          showIcon
          message={t`Could not load your Agents`}
          description={i18n._(loadError)}
          action={
            <Button size="small" onClick={() => void loadCandidates()}>
              <Trans>Retry</Trans>
            </Button>
          }
        />
      )}
      {submitError && (
        <Alert
          type="error"
          showIcon
          message={t`Could not add the Agent`}
          description={i18n._(submitError)}
        />
      )}
      {refreshError && (
        <Alert
          type="warning"
          showIcon
          message={t`Agent added, but the Project member list could not be refreshed`}
          description={i18n._(refreshError)}
          action={
            <Button size="small" onClick={() => void refreshRoster()}>
              <Trans>Retry refresh</Trans>
            </Button>
          }
        />
      )}
      {!loading && !loadError && !refreshError && agents.length === 0 && (
        <p>
          <Trans>No eligible Agents are available.</Trans>
        </p>
      )}
      {!loading && !loadError && !refreshError && agents.length > 0 && (
        <div
          role="radiogroup"
          aria-label={t`Your active Agents`}
          aria-describedby="add-owned-agent-description"
        >
          {agents.map((agent, index) => (
            <label key={agent.id} style={{ display: 'block', marginBottom: 8 }}>
              <input
                type="radio"
                name="owned-agent"
                value={agent.id}
                autoFocus={index === 0}
                checked={selectedId === agent.id}
                onChange={() => setSelectedId(agent.id)}
                disabled={submitting}
              />{' '}
              {agent.display_name}
            </label>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
        <Button onClick={handleCancel} disabled={submitting}>
          <Trans>Cancel</Trans>
        </Button>
        <Button
          type="primary"
          onClick={() => void submit()}
          disabled={!selectedId || submitting || !!refreshError}
          loading={submitting}
        >
          <Trans>Add Agent</Trans>
        </Button>
      </div>
    </Modal>
  );
}

export default function ProjectMembersPage({ selectedMemberId, onSelectMember }: Props) {
  const { t, i18n } = useLingui();
  const members = useServerStore((s) => s.members);
  const loaded = useServerStore((s) => s.loaded);
  const loadedGuildId = useServerStore((s) => s.loadedGuildId);
  const guilds = useServerStore((s) => s.guilds);
  const membershipError = useServerStore((s) => s.membershipError);
  const membershipRefreshing = useServerStore((s) => s.membershipRefreshing);
  const reconcileMembership = useServerStore((s) => s.reconcileMembership);
  const agents = useAgentStore((s) => s.agents);
  const currentUser = useAuthStore((s) => s.currentUser);
  const guildId = useAuthStore((s) => s.guildId);
  const authority = useProjectAuthority(guildId);
  const [query, setQuery] = useState('');
  const [role, setRole] = useState<'all' | 'member' | 'agent'>('all');
  const [showAddMenu, setShowAddMenu] = useState(false);
  const [addExistingDialog, setAddExistingDialog] = useState<{
    guildId: string;
    currentUserId: string;
    memberIds: Set<string>;
  } | null>(null);
  const [inviteDialog, setInviteDialog] = useState<{
    guildId: string;
    currentUserId: string;
  } | null>(null);
  const [profileMemberId, setProfileMemberId] = useState<string | null>(null);
  const [memberMenuId, setMemberMenuId] = useState<string | null>(null);
  const [memberDialogId, setMemberDialogId] = useState<string | null>(null);
  const projectOwnerId = guilds.find((guild) => guild.id === guildId)?.owner_id;
  const hasCurrentRoster =
    loaded &&
    loadedGuildId === guildId &&
    !!currentUser &&
    members.some((member) => member.user_id === currentUser.id);
  const canManageMembers =
    authority.ready && authority.canManageProject && hasCurrentRoster && !!currentUser;
  const people = useMemo(
    () =>
      members.filter((member) => {
        const matchesRole = role === 'all' || member.role === role;
        return (
          matchesRole && member.display_name.toLowerCase().includes(query.trim().toLowerCase())
        );
      }),
    [members, query, role],
  );

  useEffect(() => {
    if (!addExistingDialog) return;
    const sameSession =
      guildId === addExistingDialog.guildId && currentUser?.id === addExistingDialog.currentUserId;
    if (!sameSession || (loaded && !canManageMembers)) setAddExistingDialog(null);
  }, [addExistingDialog, canManageMembers, currentUser?.id, guildId, loaded]);

  useEffect(() => {
    if (!authority.ready || !authority.canManageProject) {
      setMemberMenuId(null);
      setMemberDialogId(null);
    }
  }, [authority.canManageProject, authority.ready]);

  useEffect(() => {
    if (canManageMembers) return;
    setShowAddMenu(false);
    setInviteDialog(null);
  }, [canManageMembers]);

  useEffect(() => {
    if (!inviteDialog) return;
    if (
      inviteDialog.guildId !== guildId ||
      inviteDialog.currentUserId !== currentUser?.id ||
      !canManageMembers
    )
      setInviteDialog(null);
  }, [canManageMembers, currentUser?.id, guildId, inviteDialog]);

  const openInvite = () => {
    if (!canManageMembers || !guildId || !currentUser) return;
    setShowAddMenu(false);
    setInviteDialog({ guildId, currentUserId: currentUser.id });
  };
  const openAgentPicker = () => {
    if (!canManageMembers || !guildId || !currentUser) return;
    setShowAddMenu(false);
    setAddExistingDialog({
      guildId,
      currentUserId: currentUser.id,
      memberIds: new Set(members.map((member) => member.user_id)),
    });
  };
  return (
    <main className={surface.pageRoot} aria-label={t`Project members`}>
      <ProjectPageHeader
        title={t`Members`}
        end={
          canManageMembers && guildId ? (
            <div className={styles.addMenu}>
              <Button
                type="primary"
                icon={<PlusOutlined aria-hidden="true" />}
                aria-label={t`Add member`}
                onClick={() => setShowAddMenu((open) => !open)}
              >
                <Trans>Add member</Trans>
              </Button>
              {showAddMenu && (
                <div role="menu" aria-label={t`Add member options`}>
                  <button type="button" role="menuitem" onClick={openInvite}>
                    <Trans>Invite human</Trans>
                  </button>
                  {canManageMembers && (
                    <button type="button" role="menuitem" onClick={openAgentPicker}>
                      <Trans>Add Agent</Trans>
                    </button>
                  )}
                </div>
              )}
            </div>
          ) : null
        }
      />
      <section className={`${surface.pageContainer} ${styles.people}`}>
        {membershipError && guildId && (
          <Alert
            type="warning"
            showIcon
            title={i18n._(membershipError)}
            action={
              <Button
                size="small"
                loading={membershipRefreshing}
                disabled={membershipRefreshing}
                onClick={() => void reconcileMembership(guildId).catch(() => undefined)}
              >
                <Trans>Retry refresh</Trans>
              </Button>
            }
          />
        )}
        <header>
          <div>
            <h2>
              <Trans>People</Trans>
            </h2>
            <span>
              <Plural value={people.length} one="# visible member" other="# visible members" />
            </span>
          </div>
        </header>
        <div className={styles.filters}>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t`Search people…`}
            aria-label={t`Search members`}
          />
          {(['all', 'member', 'agent'] as const).map((value) => (
            <button
              type="button"
              key={value}
              onClick={() => setRole(value)}
              className={role === value ? styles.filterActive : ''}
            >
              {value === 'all' ? t`All` : value === 'agent' ? t`Agents` : t`Humans`}
            </button>
          ))}
        </div>
        <div className={surface.borderedList}>
          {people.map((member) => {
            const agent = agents.find((item) => item.id === member.user_id);
            const profileOpen = profileMemberId === member.user_id;
            const canChangeRole =
              authority.ready &&
              authority.canGovernAdmins &&
              member.role !== 'agent' &&
              member.user_id !== projectOwnerId;
            const canRemove =
              authority.ready &&
              member.user_id !== currentUser?.id &&
              member.user_id !== projectOwnerId &&
              (authority.isOwner || (authority.isAdmin && member.membership_role === 'member'));
            const menuOpen = memberMenuId === member.user_id;
            const dialogOpen = memberDialogId === member.user_id;
            return (
              <div key={member.user_id} className={styles.rowWrap}>
                <button
                  type="button"
                  className={`${surface.listRow} ${surface.listRowInteractive} ${styles.row} channel-item${selectedMemberId === member.user_id ? ` ${surface.listRowActive}` : ''}${agent?.disabled ? ' channel-item-disabled' : ''}`}
                  onClick={() => onSelectMember(member.user_id)}
                  aria-label={t`Open direct message with ${member.display_name}`}
                >
                  <UserBadge
                    userId={member.user_id}
                    displayName={member.display_name}
                    role={member.role}
                    avatarUrl={member.avatar_url}
                    state={agent?.state ?? member.state}
                    disabled={agent?.disabled}
                    size={30}
                  />
                  {member.role === 'agent' && agent?.disabled && (
                    <span className="agent-disabled-badge">{i18n._(DISABLED_LABEL)}</span>
                  )}
                  <span
                    className={`${styles.membershipBadge} ${
                      member.role === 'agent'
                        ? styles.membershipAgent
                        : member.user_id === projectOwnerId
                          ? styles.membershipOwner
                          : member.membership_role === 'admin'
                            ? styles.membershipAdmin
                            : styles.membershipMember
                    }`}
                  >
                    {i18n._(membershipLabel(member, projectOwnerId))}
                  </span>
                </button>
                <div className={styles.rowActions}>
                  <Popover
                    trigger="click"
                    placement="bottomRight"
                    autoAdjustOverflow
                    destroyOnHidden
                    getPopupContainer={() => document.body}
                    classNames={{ root: styles.profilePopover }}
                    open={profileOpen}
                    onOpenChange={(open) => setProfileMemberId(open ? member.user_id : null)}
                    content={
                      <MemberProjectProfilePopover
                        guildId={guildId!}
                        member={member}
                        isProjectOwner={member.user_id === projectOwnerId}
                      />
                    }
                  >
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={t`View ${member.display_name} profile`}
                      title={t`View Project profile`}
                    >
                      <IdcardOutlined aria-hidden="true" />
                    </button>
                  </Popover>
                  {member.user_id !== currentUser?.id && (
                    <button
                      type="button"
                      className="icon-btn"
                      aria-label={t`Open direct message with ${member.display_name}`}
                      title={t`Open direct message`}
                      onClick={() => onSelectMember(member.user_id)}
                    >
                      <MessageOutlined aria-hidden="true" />
                    </button>
                  )}
                  {(canChangeRole || canRemove) && guildId && (
                    <Popover
                      trigger="click"
                      placement="bottomRight"
                      destroyOnHidden
                      open={menuOpen}
                      onOpenChange={(open) => {
                        if (!open && dialogOpen) return;
                        setProfileMemberId(null);
                        setMemberMenuId(open ? member.user_id : null);
                      }}
                      content={
                        <div
                          className={styles.memberMenu}
                          role="menu"
                          aria-label={t`Manage ${member.display_name}`}
                        >
                          {canChangeRole && (
                            <ProjectMemberRoleControl
                              guildId={guildId}
                              userId={member.user_id}
                              displayName={member.display_name}
                              membershipRole={member.membership_role}
                              canManage={canChangeRole}
                              onDialogOpenChange={(open) => {
                                setMemberDialogId(open ? member.user_id : null);
                                if (!open) setMemberMenuId(null);
                              }}
                            />
                          )}
                          <ServerMemberRemovalControl
                            userId={member.user_id}
                            displayName={member.display_name}
                            role={member.role === 'agent' ? 'agent' : 'member'}
                            canRemove={canRemove}
                            compact
                            onDialogOpenChange={(open) => {
                              setMemberDialogId(open ? member.user_id : null);
                              if (!open) setMemberMenuId(null);
                            }}
                          />
                        </div>
                      }
                    >
                      <button
                        type="button"
                        className="icon-btn"
                        aria-label={t`Manage ${member.display_name}`}
                        aria-haspopup="menu"
                        aria-expanded={menuOpen}
                      >
                        <EllipsisOutlined aria-hidden="true" />
                      </button>
                    </Popover>
                  )}
                </div>
              </div>
            );
          })}
          {!people.length && (
            <p className={surface.listEmpty}>
              <Trans>No matching members.</Trans>
            </p>
          )}
        </div>
      </section>
      {addExistingDialog &&
        guildId === addExistingDialog.guildId &&
        currentUser?.id === addExistingDialog.currentUserId && (
          <AddOwnedAgentDialog
            guildId={addExistingDialog.guildId}
            currentUserId={addExistingDialog.currentUserId}
            memberIds={addExistingDialog.memberIds}
            onClose={() => setAddExistingDialog(null)}
          />
        )}
      {inviteDialog &&
        guildId === inviteDialog.guildId &&
        currentUser?.id === inviteDialog.currentUserId && (
          <InviteLinksModal guildId={inviteDialog.guildId} onClose={() => setInviteDialog(null)} />
        )}
    </main>
  );
}
